// Deferred consent answers for pre-existing SERVER sessions answered while the
// backend was unreachable.
//
// Two different consults lose consent in two different ways, and only one of
// them has a durable row to write to. An offline-LOCAL consult owns an
// `offline_sessions` row, so its answer is stored there and Rust carries it on
// the sync-time session PATCH. A pre-existing server session has no such row —
// `set_server_session_id` only ever stamps a row that sync itself created — so
// it never reaches that PATCH at all. Nothing carries its answer, and the
// optimistic `GET_SESSION_BY_ID` cache write dies with the app.
//
// This atom holds the clinician's answer per session id until it can be
// replayed to the backend on the reconnect edge (`useReplayPendingConsent`).
// Same mechanism `pendingTranscribeModeBySessionIdAtom` uses for the same class
// of session, deliberately, so a per-session setting that cannot be persisted
// offline has one holding pattern rather than two.
//
// Intentionally in-memory, matching the transcribe-mode store beside it: there
// is no offline DB row to persist to, and on an app restart the react-query
// cache that held the optimistic value is gone too, so there is no unsynced UI
// state left to protect.

import { atom, getDefaultStore } from 'jotai';

import { abortAllPendingConsentReplays } from './pending-consent-replay-abort';

/**
 * Map of `sessionId -> the clinician's consent answer` for server sessions
 * answered while the backend was unreachable and not yet persisted.
 *
 * Keyed presence, not a truthy value, and that is load-bearing: `false` is an
 * answer (a refusal), not an absence. The backend dumps the session PATCH with
 * `model_dump(exclude_unset=True, exclude_none=True)`, so an omitted field
 * cannot clear a stored answer while a literal `false` is written — which means
 * a store that dropped `false` would leave a refusal silently unrecorded.
 *
 * Populated by `useMutateUpdateSessionConsentById` when the server write is
 * skipped for a session that does have a server record, and drained by
 * `useReplayPendingConsent` on the reconnect edge.
 */
export const pendingConsentBySessionIdAtom = atom<Record<string, boolean>>({});

// No `debugLabel`. Assigning one at module scope throws in any suite that
// doubles `jotai` with a primitive `atom()` stub — two unrelated mutation
// suites broke on exactly that — and this module is now pulled in by the
// logout path, so it is reachable from far more suites than its own.

/**
 * Drop every deferred answer. Called from `clearLogoutStorage`.
 *
 * Being in-memory is not the same as being short-lived: neither sign-out path
 * reloads the webview (`logoutWithoutRedirect` explicitly does not, and
 * `logout` clears state and routes within the same JS context), so this map
 * survives into the next account exactly as the workspace-id atom beside it
 * does — which is why that one is nulled there rather than left to a key
 * removal.
 *
 * Left behind, the next clinician's token would be driven against the previous
 * clinician's session ids on the reconnect edge. A different org earns a 403
 * and the entry is dropped, but a second account with legitimate access to the
 * same session would take the write — attributing one clinician's consent
 * answer to another on a shared clinical workstation. Correctness must not rest
 * on the backend refusing us.
 */
export const clearPendingConsent = (): void => {
  abortAllPendingConsentReplays();
  getDefaultStore().set(pendingConsentBySessionIdAtom, {});
};
